import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Search, MapPin, TrendingUp } from "lucide-react";

interface ServicesCasesProps {
  language: 'es' | 'en';
}

export const ServicesCases = ({ language }: ServicesCasesProps) => {
  const content = {
    es: {
      title: "Casos relacionados",
      subtitle: "Ejemplos reales de cómo estos servicios han generado resultados.",
      cases: [
        {
          icon: Search,
          service: "SEO Técnico",
          industry: "Fabricante industrial",
          action: "Auditoría técnica + corrección de errores de indexación",
          result: "Incremento del 180% en tráfico orgánico en 6 meses"
        },
        {
          icon: MapPin,
          service: "SEO Local",
          industry: "Servicios profesionales B2B",
          action: "Optimización Google Business + estrategia de reseñas",
          result: "Top 3 en búsquedas locales principales"
        },
        {
          icon: TrendingUp,
          service: "Google Ads",
          industry: "Distribuidor regional",
          action: "Reestructuración de campañas + tracking avanzado",
          result: "Reducción del 45% en costo por conversión"
        }
      ],
      cta: "Ver todos los casos",
      disclaimer: "Resultados varían según industria, competencia y punto de partida."
    },
    en: {
      title: "Related cases",
      subtitle: "Real examples of how these services have generated results.",
      cases: [
        {
          icon: Search,
          service: "Technical SEO",
          industry: "Industrial manufacturer",
          action: "Technical audit + indexing error correction",
          result: "180% increase in organic traffic in 6 months"
        },
        {
          icon: MapPin,
          service: "Local SEO",
          industry: "B2B professional services",
          action: "Google Business optimization + review strategy",
          result: "Top 3 in main local searches"
        },
        {
          icon: TrendingUp,
          service: "Google Ads",
          industry: "Regional distributor",
          action: "Campaign restructuring + advanced tracking",
          result: "45% reduction in cost per conversion"
        }
      ],
      cta: "View all cases",
      disclaimer: "Results vary by industry, competition, and starting point."
    }
  };

  const t = content[language];

  return (
    <section className="section-padding bg-background">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-10">
          {t.cases.map((caso, index) => (
            <div 
              key={index}
              className="bg-secondary/30 rounded-xl p-6 border border-border"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center">
                  <caso.icon className="w-5 h-5 text-brand" />
                </div>
                <span className="text-sm font-semibold text-brand uppercase tracking-wide">
                  {caso.service}
                </span>
              </div>
              
              <p className="text-sm text-muted-foreground mb-3">
                {caso.industry}
              </p>
              
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-medium text-muted-foreground">Acción:</span>{" "}
                  <span className="text-foreground">{caso.action}</span>
                </p>
                <p>
                  <span className="font-medium text-success">Resultado:</span>{" "}
                  <span className="text-foreground font-medium">{caso.result}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center space-y-4">
          <Button 
            variant="outline" 
            size="lg"
            className="group"
            asChild
          >
            <Link to={language === 'es' ? '/casos-de-exito' : '/en/case-studies'}>
              {t.cta}
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
          <p className="text-xs text-muted-foreground">
            {t.disclaimer}
          </p>
        </div>
      </div>
    </section>
  );
};
